import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";

// Eager load critical components
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Layout } from "./components/Layout";

// Lazy load other pages for better performance
const Employees = lazy(() => import("./pages/Employees").then(m => ({ default: m.Employees })));
const EmployeeProfile = lazy(() => import("./pages/EmployeeProfile").then(m => ({ default: m.EmployeeProfile })));
const PendingApprovals = lazy(() => import("./pages/PendingApprovals").then(m => ({ default: m.PendingApprovals })));
const Leaves = lazy(() => import("./pages/Leaves").then(m => ({ default: m.Leaves })));
const WalletPage = lazy(() => import("./pages/WalletPage").then(m => ({ default: m.WalletPage })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const OnboardingWizard = lazy(() => import("./pages/OnboardingWizard").then(m => ({ default: m.OnboardingWizard })));
const JoinPage = lazy(() => import("./pages/JoinPage").then(m => ({ default: m.JoinPage })));
const TasksPage = lazy(() => import("./pages/TasksPage").then(m => ({ default: m.TasksPage })));
const DailyReportsPage = lazy(() => import("./pages/DailyReportsPage").then(m => ({ default: m.DailyReportsPage })));
const PayrollPage = lazy(() => import("./pages/PayrollPage").then(m => ({ default: m.PayrollPage })));
const PerformancePage = lazy(() => import("./pages/PerformancePage").then(m => ({ default: m.PerformancePage })));
const AdminNotificationsPage = lazy(() => import("./pages/AdminNotificationsPage").then(m => ({ default: m.AdminNotificationsPage })));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })));
const JobsPage = lazy(() => import("./pages/JobsPage").then(m => ({ default: m.JobsPage })));
const VenturesPage = lazy(() => import("./pages/VenturesPage").then(m => ({ default: m.VenturesPage })));
const DocumentVaultPage = lazy(() => import("./pages/DocumentVaultPage"));
const WelcomeModal = lazy(() => import("./components/WelcomeModal").then(m => ({ default: m.WelcomeModal })));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// New Modules
const ShiftManagement = lazy(() => import("./pages/ShiftManagement"));
const HolidaysCalendar = lazy(() => import("./pages/HolidaysCalendar"));
const EmployeeDirectory = lazy(() => import("./pages/EmployeeDirectory"));
const FeedbackSurveys = lazy(() => import("./pages/FeedbackSurveys"));
const RecognitionAwards = lazy(() => import("./pages/RecognitionAwards"));
const PoliciesHandbook = lazy(() => import("./pages/PoliciesHandbook"));
const ReportsExport = lazy(() => import("./pages/ReportsExport"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const ActivityLogsPage = lazy(() => import("./pages/ActivityLogsPage"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const CareersPage = lazy(() => import("./pages/CareersPage"));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      if (response.data.is_first_login && response.data.status === "active") {
        setShowWelcome(true);
      }
    } catch (error) {
      console.error("Auth error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      if (userData.is_first_login && userData.status === "active") {
        setShowWelcome(true);
      }
      toast.success(`Welcome back, ${userData.first_name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      toast.success("Account created successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
    toast.info("Logged out successfully");
  };

  const completeFirstLogin = async () => {
    try {
      await axios.post(`${API}/auth/first-login-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(prev => ({ ...prev, is_first_login: false }));
      setShowWelcome(false);
    } catch (error) {
      console.error("Error completing first login:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      register, 
      logout, 
      loading,
      showWelcome,
      setShowWelcome,
      completeFirstLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-primary/20 rounded-full" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Skip onboarding redirect if admin is impersonating (impersonation_mode in localStorage)
  const isImpersonating = localStorage.getItem("impersonation_mode") === "true";
  
  // Redirect to onboarding if not completed and status is onboarding (but not when impersonating)
  if (!isImpersonating && !user.onboarding_completed && user.status === "onboarding" && !location.pathname.includes("/onboarding")) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Main App Content
const AppContent = () => {
  const { user, showWelcome, completeFirstLogin } = useAuth();

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/employees" element={<Employees />} />
                      <Route path="/employees/:id" element={<EmployeeProfile />} />
                      <Route path="/approvals" element={<PendingApprovals />} />
                      <Route path="/leaves" element={<Leaves />} />
                      <Route path="/wallet" element={<WalletPage />} />
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/daily-reports" element={<DailyReportsPage />} />
                      <Route path="/payroll" element={<PayrollPage />} />
                      <Route path="/performance" element={<PerformancePage />} />
                      <Route path="/notifications/send" element={<AdminNotificationsPage />} />
                      <Route path="/analytics" element={<AnalyticsPage />} />
                      <Route path="/jobs" element={<JobsPage />} />
                      <Route path="/ventures" element={<VenturesPage />} />
                      <Route path="/documents" element={<DocumentVaultPage />} />
                      <Route path="/shifts" element={<ShiftManagement />} />
                      <Route path="/holidays" element={<HolidaysCalendar />} />
                      <Route path="/directory" element={<EmployeeDirectory />} />
                      <Route path="/surveys" element={<FeedbackSurveys />} />
                      <Route path="/recognition" element={<RecognitionAwards />} />
                      <Route path="/policies" element={<PoliciesHandbook />} />
                      <Route path="/reports" element={<ReportsExport />} />
                      <Route path="/attendance" element={<AttendancePage />} />
                      <Route path="/activity-logs" element={<ActivityLogsPage />} />
                      <Route path="/team" element={<TeamDashboard />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      
      {showWelcome && user && (
        <Suspense fallback={null}>
          <WelcomeModal 
            user={user} 
            onClose={completeFirstLogin} 
          />
        </Suspense>
      )}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            duration: 4000,
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
